import { useState, useEffect, useRef, useCallback } from 'react';
import { MeetingData, MeetingFormData, AttendeePriority, Employee } from '../interfaces/interface';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { EmployeeService } from '../services/EmployeeService';
import { loadAttendeesInBatches, computeAuthorizedLogins } from '../utils/attendeeUtils';
import { logger } from 'src/logger';

interface UseSharedMeetingProps {
  alias: string;
  meetingId: string | null;
  creator: string | null;
  addFlashMessage: (type: 'success' | 'error', content: string) => void;
  setMeetingFormData: React.Dispatch<React.SetStateAction<MeetingFormData>>;
  handleAddAttendee: (employee: Employee, priority: AttendeePriority) => Promise<void> | void;
  /** Callback to set the initial snapshot for unsaved-changes detection */
  setInitialSnapshot: (snapshot: string) => void;
}

interface UseSharedMeetingResult {
  isSharedLink: boolean;
  isSharedViewer: boolean;
  isSharedCreator: boolean;
  sharedCreator: string | null;
  sharedMeetingId: string | null;
  isLoadingSharedMeeting: boolean;
  sharedMeetingProgress: string;
  loadedMeetingData: MeetingData | null;
  isAccessDenied: boolean;
}

/**
 * Manages shared meeting link state: URL param parsing, loading from DDB,
 * access control evaluation, and creator-only EA refresh.
 */
export const useSharedMeeting = ({
  alias,
  meetingId,
  creator,
  addFlashMessage,
  setMeetingFormData,
  handleAddAttendee,
  setInitialSnapshot
}: UseSharedMeetingProps): UseSharedMeetingResult => {
  const [isLoadingSharedMeeting, setIsLoadingSharedMeeting] = useState(() => Boolean(meetingId && creator));
  const [sharedMeetingProgress, setSharedMeetingProgress] = useState('');
  const [loadedMeetingData, setLoadedMeetingData] = useState<MeetingData | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const sharedMeetingLoaded = useRef(false);

  const isSharedLink = Boolean(meetingId && creator);
  const isSharedCreator = Boolean(meetingId && creator && creator.toLowerCase() === alias.toLowerCase());
  const isSharedViewer = Boolean(meetingId && creator && creator.toLowerCase() !== alias.toLowerCase());

  // Load shared meeting from DDB when URL params are present
  useEffect(() => {
    if (sharedMeetingLoaded.current) return;
    if (!meetingId || !creator) return;

    sharedMeetingLoaded.current = true;
    setIsLoadingSharedMeeting(true);

    let cancelled = false;

    const loadSharedMeeting = async () => {
      try {
        setSharedMeetingProgress('Verifying access...');
        const meetingData = await EmployeeSettingsService.getMeeting(creator, meetingId);
        if (cancelled) return;
        if (!meetingData) {
          addFlashMessage('error', 'Meeting not found. The link may be invalid or expired.');
          return;
        }

        // Access control: check if current user is authorized
        const isCreator = creator.toLowerCase() === alias.toLowerCase();
        if (!isCreator) {
          const authorized = meetingData.authorizedLogins;
          const isAuthorized =
            authorized === undefined
              ? // Old meeting without the field: fall back to attendee list
                meetingData.attendees.some((a) => a.loginName.toLowerCase() === alias.toLowerCase())
              : authorized.length === 0
              ? // Explicitly empty: deny access
                false
              : // Check authorized list
                authorized.some((login) => login.toLowerCase() === alias.toLowerCase());

          if (!isAuthorized) {
            setIsAccessDenied(true);
            return;
          }
        }

        setSharedMeetingProgress('Loading meeting details...');
        setLoadedMeetingData(meetingData);

        // Pre-populate form data from saved meeting
        setMeetingFormData((prev) => ({
          ...prev,
          ...(meetingData.subject ? { subject: meetingData.subject } : {}),
          agenda: meetingData.agenda ?? prev.agenda,
          duration: meetingData.duration ?? prev.duration,
          repeat: meetingData.repeat ?? prev.repeat,
          onBehalfOfLogin: meetingData.onBehalfOfLogin ?? prev.onBehalfOfLogin
        }));

        if (cancelled) return;

        setSharedMeetingProgress(`Loading attendees (0/${meetingData.attendees.length})...`);
        await loadAttendeesInBatches(meetingData.attendees, handleAddAttendee, setSharedMeetingProgress);

        if (cancelled) return;

        setSharedMeetingProgress('Loading scheduling data...');

        // Set initial snapshot for unsaved changes detection
        const initialSnapshot = JSON.stringify(
          meetingData.attendees
            .map((a) => ({ login: a.loginName, priority: a.priority, proxyForLogin: a.proxyForLogin }))
            .sort((a, b) => a.login.localeCompare(b.login))
        );
        setInitialSnapshot(initialSnapshot);

        // Creator-only: background EA refresh to keep authorizedLogins up to date
        if (isCreator) {
          refreshEaLogins(creator, meetingId, meetingData);
        }

        logger.info('Shared meeting loaded from URL', { meetingId, creator, attendeeCount: meetingData.attendees.length });
      } catch (error) {
        if (cancelled) return;
        logger.error('Failed to load shared meeting', { error, meetingId, creator });
        addFlashMessage('error', 'Failed to load meeting data. Please try again.');
      } finally {
        if (!cancelled) {
          setIsLoadingSharedMeeting(false);
          setSharedMeetingProgress('');
        }
      }
    };

    loadSharedMeeting();

    return () => {
      cancelled = true;
      sharedMeetingLoaded.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, creator, alias]);

  /**
   * Mark the shared meeting as loaded from the outside (e.g. after URL params
   * are set by ensureMeetingCreated) so the load useEffect doesn't re-fire.
   */
  const markAsLoaded = useCallback(() => {
    sharedMeetingLoaded.current = true;
  }, []);

  return {
    isSharedLink,
    isSharedViewer,
    isSharedCreator,
    sharedCreator: creator,
    sharedMeetingId: meetingId,
    isLoadingSharedMeeting,
    sharedMeetingProgress,
    loadedMeetingData,
    isAccessDenied
  };
};

/**
 * Background EA refresh (creator-only, fire-and-forget).
 * Re-resolves EA for each attendee using the cached EmployeeService (1hr DDB cache).
 * If any eaLogin changed, updates the meeting's attendees and authorizedLogins in DDB.
 */
async function refreshEaLogins(creatorAlias: string, meetingId: string, meetingData: MeetingData): Promise<void> {
  try {
    let changed = false;
    const updatedAttendees = await Promise.all(
      meetingData.attendees.map(async (attendee) => {
        try {
          const employee = await EmployeeService.getEmployeeByLogin(attendee.loginName);
          if (!employee) return attendee;
          const ea = await EmployeeService.findExecutiveAssistant(employee);
          const newEaLogin = ea?.login_name || undefined;
          if ((newEaLogin ?? '') !== (attendee.eaLogin ?? '')) {
            changed = true;
            return { ...attendee, eaLogin: newEaLogin };
          }
        } catch {
          // Keep existing eaLogin on failure
        }
        return attendee;
      })
    );

    if (!changed) return;

    const newAuthorizedLogins = computeAuthorizedLogins(creatorAlias, updatedAttendees, {
      organizerLogin: meetingData.organizerLogin,
      onBehalfOfLogin: meetingData.onBehalfOfLogin
    });
    await EmployeeSettingsService.saveMeeting(creatorAlias, {
      ...meetingData,
      attendees: updatedAttendees,
      authorizedLogins: newAuthorizedLogins
    });
    logger.info('EA logins refreshed for meeting', { meetingId, updatedCount: updatedAttendees.length });
  } catch (error) {
    logger.warn('Background EA refresh failed', { error, meetingId });
  }
}
